
import React from 'react';
import { Bar, BarChart, Cell, ResponsiveContainer, XAxis, YAxis, LabelList } from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ChartContainer, ChartTooltip } from '@/components/ui/chart';
import { Users } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { SENDER_COLORS } from './colors';

type SenderData = {
  name: string;
  value: number;
}

interface TopSendersChartProps {
  data: SenderData[];
}

const TopSendersChart: React.FC<TopSendersChartProps> = ({ data }) => {
  const isMobile = useIsMobile();

  // Format and preprocess sender data for display
  const processedData = React.useMemo(() => {
    return data.map((item, index) => {
      // Extract email and name parts from sender string
      const emailMatch = item.name.match(/<([^>]+)>/);
      const email = emailMatch ? emailMatch[1] : item.name;
      let displayName = item.name.replace(/<.+>/, '').trim();
      
      // If no name is available, use email username part
      if (!displayName && email) {
        displayName = email.split('@')[0];
      }
      
      // Truncate display name if needed
      const truncatedName = isMobile && displayName.length > 12 
        ? `${displayName.substring(0, 10)}...` 
        : displayName;
        
      return {
        ...item,
        email,
        displayName: truncatedName,
        fullName: displayName,
        color: SENDER_COLORS[index % SENDER_COLORS.length]
      };
    });
  }, [data, isMobile]);

  return (
    <Card className="shadow-sm hover:shadow-md transition-all duration-300 border border-border/50">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
            <Users className="h-4 w-4 text-primary" />
          </div>
          <CardTitle className="text-lg font-semibold">Top Senders</CardTitle>
        </div>
        <CardDescription>Most frequent attachment senders</CardDescription>
      </CardHeader>
      <CardContent className="pt-0 pb-4">
        {processedData.length > 0 ? (
          <div className="h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart 
                data={processedData} 
                layout="vertical" 
                margin={{ 
                  top: 15, 
                  right: 20,
                  left: isMobile ? 10 : 40, 
                  bottom: 15 
                }}
                barGap={8}
                barCategoryGap={16}
              >
                <YAxis 
                  dataKey="displayName" 
                  type="category" 
                  width={isMobile ? 30 : 120}
                  axisLine={false}
                  tickLine={false}
                  tick={{ 
                    fill: '#666666', 
                    fontSize: 13,
                    fontWeight: 500,
                  }}
                  tickFormatter={(value, index) => {
                    // Return shortened display name
                    return processedData[index]?.displayName || value;
                  }}
                />
                <XAxis 
                  type="number" 
                  domain={[0, 'dataMax']} 
                  hide={true}
                />
                <ChartTooltip 
                  cursor={{ fill: 'rgba(0, 0, 0, 0.04)' }}
                  content={({ active, payload }) => 
                    active && payload && payload.length ? (
                      <div className="rounded-lg border bg-background p-3 shadow-md">
                        <div className="flex items-center gap-2 mb-1">
                          <div 
                            className="h-3 w-3 rounded-full" 
                            style={{ backgroundColor: payload[0].payload.color }}
                          />
                          <span className="font-medium">
                            {payload[0].payload.fullName}
                          </span>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {payload[0].payload.email}
                        </div>
                        <div className="text-sm font-medium mt-2">
                          {payload[0].value} attachment{payload[0].value !== 1 ? 's' : ''}
                        </div>
                      </div>
                    ) : null
                  } 
                />
                <Bar 
                  dataKey="value" 
                  radius={[6, 6, 6, 6]}
                  barSize={36}
                  animationDuration={1200}
                  className="drop-shadow-sm hover:opacity-95 transition-opacity duration-200"
                >
                  {processedData.map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={entry.color}
                      stroke={entry.color}
                      strokeWidth={0}
                    />
                  ))}
                  <LabelList 
                    dataKey="value" 
                    position="center"
                    fill="#fff"
                    style={{ fontWeight: 600, textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="flex items-center justify-center h-64 text-muted-foreground">
            No sender data available
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TopSendersChart;
